import { useContext, useEffect, useRef, useState } from "react";
import { MultiSelect } from "react-multi-select-component";
import { useServerPagination } from "../../../../hooks/customHook/paginationHook/useServerPagination";
import { useNavigate } from "react-router-dom";
import BASE_URL from "../../../../assets/global/url";
import { PaginationControls } from "../../../../hooks/customHook/paginationHook/usePagination";
import DataTable from "react-data-table-component";
import { customStyles } from "../../../../assets/global/table-style";
import { RefreshContext } from "../../Payable";

import FilterStore from "../../../../hooks/customHook/FilterStore";
import { useServerSort } from "../../../../hooks/customHook/sortingHook/useTableSort";
import { useBulkSelection } from "../../../../hooks/customHook/useBulkSelection";
import { runBulkApproval } from "../../../../utils/bulkApproval";
import BulkActionBar from "../../../../components/BulkActionBar";

const PendingStatusTab = ({
  selectedCutOff,
  currencyId,
  vendorDataFilter,
  setSearchTermFilter,
  columns,
  clearFilterSignal, // incremented by parent's handleClearFilter
  authrztn = [],
  userLoggedID,
  onBulkDone = () => {},
}) => {
  const refreshKey = useContext(RefreshContext);
  const navigate = useNavigate();
  const debounceTimer = useRef();
  const pageRestored = useRef(false);

  // --- Filter retention ---
  const [searchText, setSearchText] = useState(() =>
    FilterStore.PMgetSearch(`payable`),
  );
  useEffect(() => {
    FilterStore.PMsaveSearch(searchText, `payable`);
  }, [searchText]);

  const [filterColumn, setFilterColumn] = useState(() =>
    FilterStore.PMgetFilterColumn(`payable_pending`),
  );
  useEffect(() => {
    FilterStore.PMsaveFilterColumn(filterColumn, `payable_pending`);
  }, [filterColumn]);

  const [selectedVendor, setSelectedVendor] = useState(() =>
    FilterStore.getSelected(),
  );
  useEffect(() => {
    FilterStore.saveSelected(selectedVendor);
  }, [selectedVendor]);

  // Pending always shows ALL pending payables — the date range picker is
  // hidden on this tab, so never send a date restriction (even if the
  // Approved tab saved one in FilterStore). Backend skips the purchaseDate
  // filter when startDate/endDate are null.
  const getPendingDateRange = () => ({ startDate: null, endDate: null });

  const pendingPayablePagination = useServerPagination(
    `${BASE_URL}/payable/fetchPayableSs`,
    FilterStore.PMgetPerPage(`payable_pending`),
  );

  const sort = useServerSort(pendingPayablePagination);

  // Bulk approve/reject — approvers only
  const canBulkApprove = authrztn.includes("Payable-Approve");
  const bulk = useBulkSelection(pendingPayablePagination.data);

  const handleBulkAction = (action) =>
    runBulkApproval({
      action,
      rows: bulk.selectedRows,
      url: `${BASE_URL}/payable/${action === "approve" ? "bulk-approved" : "bulk-rejected"}`,
      userLoggedID,
      getId: (row) => row.id,
      getAmount: (row) => row.totalPrice,
      getCurrencyName: (row) => row.currency?.currency_name,
      setIsProcessing: bulk.setIsProcessing,
      onDone: onBulkDone,
    });

  // --- Page / PerPage retention ---
  // Save page — gated behind pageRestored so the hook's internal reset-to-1
  // from updateParams on mount doesn't clobber the stored page.
  useEffect(() => {
    if (!pageRestored.current) return;
    if (pendingPayablePagination.currentPage) {
      FilterStore.PMsavePage(
        pendingPayablePagination.currentPage,
        `payable_pending`,
      );
    }
  }, [pendingPayablePagination.currentPage]);

  useEffect(() => {
    if (pendingPayablePagination.itemsPerPage) {
      FilterStore.PMsavePerPage(
        pendingPayablePagination.itemsPerPage,
        `payable_pending`,
      );
    }
  }, [pendingPayablePagination.itemsPerPage]);

  // Restore last viewed page on mount. setTimeout(600) defers until after the
  // mount-time fetchPayable → updateParams → hook reset-to-1 has settled,
  // then opens the save gate for user navigation.
  useEffect(() => {
    const savedPage = FilterStore.PMgetPage(`payable_pending`);
    if (savedPage > 1) {
      setTimeout(() => {
        pendingPayablePagination.setCurrentPage(savedPage);
        pageRestored.current = true;
      }, 600);
    } else {
      setTimeout(() => {
        pageRestored.current = true;
      }, 600);
    }
  }, []);

  // --- Vendor filter ---
  const handleVendorChange = (selected) => {
    setSelectedVendor(selected);

    pendingPayablePagination.updateApiUrl(`${BASE_URL}/payable/fetchPayableSs`);
    pendingPayablePagination.updateParams({
      ...getPendingDateRange(),
      currencyId,
      selectedVendor: selected,
      status: "Pending",
    });
  };

  // --- Search ---
  const handleSearch = (value) => {
    const raw = String(value).replace(/,/g, "");

    // Format with thousands separators for display
    const [intPart, decimalPart] = raw.split(".");
    const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const formatted =
      decimalPart !== undefined ? `${withCommas}.${decimalPart}` : withCommas;

    const isMatchingTotalAmount = pendingPayablePagination.data.some((item) =>
      String(item.totalPrice).includes(raw),
    );

    let searchValue;
    if (isMatchingTotalAmount) {
      setSearchText(formatted);
      searchValue = formatted;
    } else {
      setSearchText(raw);
      searchValue = raw;
    }

    clearTimeout(debounceTimer.current);

    // Search is CLEARED — go back to the full pending list (vendor filter kept).
    // Pending never touches the shared selectedCutOff — that date range
    // belongs to the Approved tab.
    if (raw.trim() === "") {
      pendingPayablePagination.updateApiUrl(
        `${BASE_URL}/payable/fetchPayableSs`,
      );
      pendingPayablePagination.updateParams({
        ...getPendingDateRange(),
        currencyId,
        selectedVendor,
        status: "Pending",
      });
      return;
    }


    debounceTimer.current = setTimeout(() => {
      pendingPayablePagination.updateApiUrl(
        `${BASE_URL}/payable/fetchPayableSearch`,
      );
      pendingPayablePagination.updateParams({
        // Always pass null dates during search so results are not date-restricted.
        // The date range display (Point 2) is updated separately from results.
        startDate: null,
        endDate: null,
        filterColumn: filterColumn,
        searchText: searchValue,
        currencyId,
        status: "Pending",
      });
    }, 500);
  };

  // --- Clear Filter signal from parent ---
  // When the parent fires handleClearFilter it increments clearFilterSignal.
  // Reset search text, vendor selection, filter column, and the prior-cutoff
  // ref so the tab is back to a clean state matching the "show all" cutoff.
  useEffect(() => {
    if (clearFilterSignal === 0) return; // skip the initial render
    setSearchText("");
    FilterStore.PMsaveSearch("", "payable");
    setSelectedVendor([]);
    FilterStore.saveSelected([]);
    setFilterColumn("all");
    FilterStore.PMsaveFilterColumn("all", "payable_pending");
  }, [clearFilterSignal]);

  // --- Main data fetch (triggered by filter/cutoff/currency/refresh changes) ---
  const fetchPayable = () => {
    // Point 2 loop guard: when the user is actively searching, always pass null
    // dates so that a selectedCutOff update triggered by the results-range
    // useEffect above does not re-restrict the search results. Without this
    // guard the cycle would be: results → update cutoff → re-fetch with dates
    // → fewer results → new dates → re-fetch … (infinite loop).
    const isSearching = searchText && searchText.trim() !== "";

    let apiUrl = `${BASE_URL}/payable/fetchPayableSs`;

    if (isSearching) {
      apiUrl = `${BASE_URL}/payable/fetchPayableSearch`;
    } else if (selectedVendor && selectedVendor.length > 0) {
      apiUrl = `${BASE_URL}/payable/fetchPayableSs`;
    }

    pendingPayablePagination.updateApiUrl(apiUrl);
    pendingPayablePagination.updateParams({
      ...(isSearching
        ? { startDate: null, endDate: null }
        : getPendingDateRange(selectedCutOff)),
      currencyId,
      filterColumn: filterColumn,
      searchText: searchText,
      selectedVendor,
      status: "Pending",
    });
  };

  useEffect(() => {
    fetchPayable();
  }, [selectedVendor, currencyId, selectedCutOff, refreshKey]);

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-sm-12 col-md-4">
          <span>Suppliers</span>
          <MultiSelect
            options={vendorDataFilter}
            value={selectedVendor}
            onChange={handleVendorChange}
            labelledBy="Select"
            className="w-100"
            style={{ maxWidth: "1000px" }}
            filterOptions={(options, filter) => {
              setSearchTermFilter(filter);
              return options.filter(({ label }) =>
                label.toLowerCase().includes(filter.toLowerCase()),
              );
            }}
          />
        </div>
      </div>

      <div className="input-group my-3">
        <input
          type="text"
          className="form-control"
          placeholder="Search"
          value={searchText}
          onChange={(e) => handleSearch(e.target.value)}
        />

        <button
          type="button"
          className="btn btn-outline-secondary dropdown-toggle-split"
          data-bs-toggle="dropdown"
          aria-expanded="false"
        >
          <i className="fa-solid fa-sliders"></i>
        </button>
        <ul className="dropdown-menu dropdown-menu-end">
          <li>
            <button
              className={`dropdown-item ${filterColumn === "all" ? "active" : ""}`}
              onClick={() => setFilterColumn("all")}
            >
              All
            </button>
          </li>
          <li>
            <button
              className={`dropdown-item ${filterColumn === "product" ? "active" : ""}`}
              onClick={() => setFilterColumn("product")}
            >
              Product Name/Code
            </button>
          </li>
          <li>
            <button
              className={`dropdown-item ${filterColumn === "transaction_date" ? "active" : ""}`}
              onClick={() => setFilterColumn("transaction_date")}
            >
              Transaction Date
            </button>
          </li>
          <li>
            <button
              className={`dropdown-item ${filterColumn === "transaction_id" ? "active" : ""}`}
              onClick={() => setFilterColumn("transaction_id")}
            >
              Transaction No.
            </button>
          </li>
          <li>
            <button
              className={`dropdown-item ${filterColumn === "warehouse.name" ? "active" : ""}`}
              onClick={() => setFilterColumn("warehouse.name")}
            >
              Receiving Warehouse
            </button>
          </li>
          <li>
            <button
              className={`dropdown-item ${filterColumn === "MOP" ? "active" : ""}`}
              onClick={() => setFilterColumn("MOP")}
            >
              Mode of Payment
            </button>
          </li>
          <li>
            <button
              className={`dropdown-item ${filterColumn === "vendor.company_name" ? "active" : ""}`}
              onClick={() => setFilterColumn("vendor.company_name")}
            >
              Supplier
            </button>
          </li>
          <li>
            <button
              className={`dropdown-item ${filterColumn === "discount_value" ? "active" : ""}`}
              onClick={() => setFilterColumn("discount_value")}
            >
              Discount
            </button>
          </li>
          <li>
            <button
              className={`dropdown-item ${filterColumn === "due_date" ? "active" : ""}`}
              onClick={() => setFilterColumn("due_date")}
            >
              Deadline
            </button>
          </li>
          <li>
            <button
              className={`dropdown-item ${filterColumn === "purchaseDate" ? "active" : ""}`}
              onClick={() => setFilterColumn("purchaseDate")}
            >
              Purchase Date
            </button>
          </li>
          <li>
            <button
              className={`dropdown-item ${filterColumn === "totalPrice" ? "active" : ""}`}
              onClick={() => setFilterColumn("totalPrice")}
            >
              Total Price
            </button>
          </li>
        </ul>
      </div>

      <div className="data-table-cell-width">
        {canBulkApprove && (
          <BulkActionBar
            selectedCount={bulk.selectedRows.length}
            isProcessing={bulk.isProcessing}
            onApprove={() => handleBulkAction("approve")}
            onReject={() => handleBulkAction("reject")}
          />
        )}
        <DataTable
          columns={sort.sortColumns(columns)}
          data={pendingPayablePagination.data}
          customStyles={customStyles}
          highlightOnHover
          className="dataTable"
          {...(canBulkApprove && bulk.tableProps)}
          onRowClicked={(row) => {
            if (row.client_transaction_id?.startsWith("ROT-PO-")) {
              navigate(`../purchases/re-open-tickets/${row.id}?page=${1}`, {
                state: { isViewMode: true },
              });
            } else {
              navigate(`../purchases/view_payable/${row.id}?page=${1}`);
            }
          }}
          {...sort.sortProps}
        />
        <PaginationControls {...pendingPayablePagination} />
      </div>
    </div>
  );
};

export default PendingStatusTab;
